import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid } from 'semantic-ui-react';
import { UpsertCountryDto } from 'ts-types/api.types';
import { addCountry, editCountry } from 'services/countryServices';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { composeValidators, maxDigits, mustBeNumber, required, validateOnlyIfPresent } from 'utils/validatorUtils';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import styled from 'styled-components';

const UpserCountryFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  countryId?: number,
  country?: UpsertCountryDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertCountryForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<UpsertCountryDto>) => {

    let saveValues: Partial<UpsertCountryDto> = {...values};

    const {countryId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general"),
        cancelTokenSource
      })
    };

    if (countryId) {
      await editCountry(countryId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addCountry(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  initialCountryValues = (): Partial<UpsertCountryDto> => {
    return {
      active: true,
      isDefault: false
    };
  };

  render(): React.ReactNode {

    const {country = this.initialCountryValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={country}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    const customValidator = validateOnlyIfPresent(composeValidators(maxDigits(5), mustBeNumber));

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpserCountryFormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={12}></Grid.Column>
                  <Grid.Column width={4} textAlign="right">
                    <Field
                        name="active"
                        component={CheckBox}
                        label={getMessage("active")}
                        toggle
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("country.name")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="name"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("country.short")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="shortName"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("country.isoCode")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="isoCode"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("country.telCode")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="telCountryCode"
                        component={Input}
                        onKeyDown={ensureOnlyNumericInput}
                        validate={customValidator}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="isDefault"
                        component={CheckBox}
                        label={`${getMessage("country.isDefault")}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpserCountryFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertCountryForm);
